import React, {
  useState, useRef, useContext, useCallback,
} from 'react';
import { Animated } from 'react-native';

import { DraggableFlatListContext } from './DraggableFlatListContext';
import { ScrollViewContext } from './ScrollViewContext';
import { DISTANCE_TO_DECIDE_RESPONDER } from '../constants';
import {
  DragAndDropContext, STATUS, INITIAL_STATE, Consumer,
} from './DragAndDropContext-common';


export {
  DragAndDropContext, STATUS, INITIAL_STATE, Consumer,
} from './DragAndDropContext-common';


export const Provider = ({
  children,
}) => {
  const {
          data,
          itemHeight,
          relativeY,
          onReorder,
          snapToAnimationDuration,
          onDropHorizontal,
          headerComponentOffset,
        } = useContext(DraggableFlatListContext),
        {
          contentOffset,
        } = useContext(ScrollViewContext);

  const [active, setActive] = useState(INITIAL_STATE),
        [animatedDrag, setAnimatedDrag] = useState(null),
        webLongPress = useRef(false),
        [statefulWebLongPress, setStatefulWebLongPress] = useState(false),
        [draggedY, setDraggedY] = useState(new Animated.Value(0)),
        draggedAnimation = useRef(new Animated.Value(0));

  const setPosition = useCallback((nativeEvent, touchStatus) => {
    const positionInScrollView = nativeEvent.y + contentOffset.current.y - headerComponentOffset,
          index = Math.floor(positionInScrollView / itemHeight),
          item = data[index],
          offsetY = positionInScrollView - (itemHeight * index);

    if (!item || item.isCalendarEvent) {
      return false;
    }

    if (touchStatus === STATUS.DRAGGED && (item.status === 'done' || item.status === 'deleted')) {
      return false;
    }

    const newActive = {
      index,
      item,
      offsetY,
      touchStatus,
    };

    relativeY.setValue(positionInScrollView);

    const dY = Animated.add(Animated.add(
      relativeY,
      new Animated.Value(-offsetY),
    ), new Animated.Value(-headerComponentOffset));

    setDraggedY(dY);

    if (active.touchStatus !== STATUS.DRAGGED && newActive.touchStatus === STATUS.DRAGGED) {
      setAnimatedDrag(newActive);
      Animated.timing(draggedAnimation.current,
        {
          toValue: 1,
          duration: snapToAnimationDuration,
          useNativeDriver: true,
        }).start();
    }

    setActive(newActive);
    return true;
  }, [active.touchStatus, contentOffset, data, headerComponentOffset, itemHeight, relativeY, snapToAnimationDuration]);

  const checkForHorizontalSwipe = useCallback((nativeEvent) => {
    if ((active.touchStatus === STATUS.TOUCHED || active.touchStatus === STATUS.INACTIVE)
      && Math.abs(nativeEvent.translationX) > DISTANCE_TO_DECIDE_RESPONDER
    ) {
      setPosition(nativeEvent, STATUS.HORIZONTAL);
    }
  }, [active.touchStatus, setPosition]);

  const finishCb = useCallback((/* finished */) => {
    if (active.touchStatus !== STATUS.DRAGGED) {
      setAnimatedDrag(INITIAL_STATE);
    }
  }, [active.touchStatus]);

  const onPan = useCallback((nativeEvent) => {
    if (active.touchStatus === STATUS.INACTIVE) {
      if (webLongPress.current) {
        setPosition(nativeEvent, STATUS.DRAGGED);
      } else {
        setPosition(nativeEvent, STATUS.TOUCHED);
      }
    }
    webLongPress.current = false;
    setStatefulWebLongPress(false);
    checkForHorizontalSwipe(nativeEvent);
  }, [active.touchStatus, checkForHorizontalSwipe, setPosition]);

  const onLongPress = useCallback((nativeEvent) => {
    if (nativeEvent.y === undefined) {
      webLongPress.current = true;
      setStatefulWebLongPress(true);
    }
  }, []);

  const onDropHorizontalInternal = useCallback((nativeEvent) => {
    const { translationX } = nativeEvent,
          { index } = active;

    onDropHorizontal && onDropHorizontal({ translationX, index });
  }, [active, onDropHorizontal]);

  const onDrop = useCallback((nativeEvent) => {
    const positionInScrollView = nativeEvent.y + contentOffset.current.y - headerComponentOffset,
          toIndex = Math.max(
            0,
            Math.min(data.length - 1, Math.floor(positionInScrollView / itemHeight)),
          ),
          fromIndex = active.index;

    if (fromIndex !== toIndex) {
      onReorder({ fromIndex, toIndex });
    }

    const translateY = draggedAnimation.current.interpolate({
      inputRange: [0, 0.8, 1],
      outputRange: [
        (toIndex * itemHeight) - contentOffset.current.y,
        (toIndex * itemHeight) - contentOffset.current.y,
        nativeEvent.y - active.offsetY - headerComponentOffset,
      ],
    });

    setDraggedY(translateY);
    Animated.timing(draggedAnimation.current,
      {
        toValue: 0,
        duration: snapToAnimationDuration,
        useNativeDriver: true,
      })
      .start(finishCb);

    setActive(INITIAL_STATE);
  }, [active.index, active.offsetY, contentOffset, data.length, finishCb, headerComponentOffset, itemHeight, onReorder, snapToAnimationDuration]);

  const reset = useCallback(() => {
    setActive(INITIAL_STATE);
    setAnimatedDrag(INITIAL_STATE);
    setStatefulWebLongPress(false);
    webLongPress.current = false;
  }, []);

  return (
    <DragAndDropContext.Provider value={{
      activeItem: active.item,
      activeItemIndex: active.index,
      animatedDrag,
      draggedAnimation: draggedAnimation.current,
      draggedY,
      statefulWebLongPress,
      itemOffsetY: active.offsetY,
      touchStatus: active.touchStatus,
      onPan,
      onLongPress,
      onDropHorizontal: onDropHorizontalInternal,
      onDrop,
      reset,
    }}
    >
      {children}
    </DragAndDropContext.Provider>
  );
};

export default { Consumer, Provider };
